import {
  InspectionSyncItemsResponse,
  InspectionSyncResponse,
} from '../api-contracts';
import { inspectionDB } from '../data/database.structure';
import { InspectionCategoryTypeModel, InspectionModel } from '../data/types';

interface ReconciledInspectionIds {
  id: number;
  inspectionCategoryTypes: InspectionCategoryTypeModel[];
}

// Merge the server's identities for an inspection into the local copy while keeping all
// locally-entered content — only ids are adopted, matched by externalId. Returns null when
// the server response does not contain the inspection.
export function mergeServerIdsIntoInspection(
  localInspection: InspectionModel,
  syncItems: InspectionSyncItemsResponse,
): ReconciledInspectionIds | null {
  const serverInspection = syncItems.inspections?.find(
    (i) => i.externalId === localInspection.externalId,
  );
  if (!serverInspection) return null;

  const serverCategoryTypes = syncItems.inspectionCategoryTypes ?? [];
  const serverObservations = syncItems.observations ?? [];

  return {
    id: serverInspection.id ?? localInspection.id,
    inspectionCategoryTypes: localInspection.inspectionCategoryTypes.map(
      (categoryType) => {
        const serverCategoryType = serverCategoryTypes.find(
          (ct) => ct.externalId === categoryType.externalId,
        );
        return {
          ...categoryType,
          id: serverCategoryType?.id ?? categoryType.id,
          observations: categoryType.observations.map((observation) => {
            const serverObservation = serverObservations.find(
              (o) => o.externalId === observation.externalId,
            );
            return {
              ...observation,
              id: serverObservation?.id ?? observation.id,
            };
          }),
        };
      },
    ),
  };
}

// Pre-submit refresh (#9416): stale local ids make the save payload re-create entities the
// server already has — buckets among them (equipment with id < 1 triggers creation). Adopt
// the server's ids for the inspection's equipment before the save payload is built; the
// Inspection record service takes the inspection's own ids.
export async function reconcileEquipmentServerIds(
  payload: InspectionSyncResponse,
): Promise<void> {
  const syncItems = payload.syncItems;
  if (!syncItems) return;

  const serverEquipments = syncItems.equipments ?? [];
  const serverEquipmentParts = syncItems.equipmentParts ?? [];

  for (const serverEquipment of serverEquipments) {
    if (!serverEquipment.externalId || serverEquipment.id === undefined)
      continue;
    const serverId = serverEquipment.id;
    const partsForEquipment = serverEquipmentParts.filter(
      (ep) => ep.equipmentId === serverId,
    );
    await inspectionDB.equipments
      .where('externalId')
      .equals(serverEquipment.externalId)
      .modify((equipment) => {
        equipment.id = serverId;
        equipment.equipmentParts = (equipment.equipmentParts ?? []).map(
          (part) => {
            const serverPart = partsForEquipment.find(
              (sp) => sp.externalId === part.externalId,
            );
            return serverPart?.id !== undefined
              ? { ...part, id: serverPart.id, equipmentId: serverId }
              : part;
          },
        );
      });
  }

  for (const serverPart of serverEquipmentParts) {
    if (!serverPart.externalId || serverPart.id === undefined) continue;
    const serverPartId = serverPart.id;
    await inspectionDB.equipmentParts
      .where('externalId')
      .equals(serverPart.externalId)
      .modify((part) => {
        part.id = serverPartId;
        if (serverPart.equipmentId !== undefined) {
          part.equipmentId = serverPart.equipmentId;
        }
      });
  }
}
